import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { XMarkIcon, PlusIcon, UserIcon, UsersIcon, CheckIcon, QuestionMarkCircleIcon, XCircleIcon, ClockIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import employeeService from '../../../services/api/employeeService';
import customersService from '../../../services/api/customersService';
import eventService from '../../../services/api/eventService';
import settingsService from '../../../services/api/settingsService';
import { useCompany } from '../../../contexts/CompanyContext';
import { usePermissions } from '../../../contexts/PermissionContext';
import { PERMISSIONS } from '../../../constants/permissionMappings';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

/**
 * EventDetailsTab - Main form for event details
 *
 * Note: formData, selectedCustomerId, and invitees state are now lifted
 * to EventSlideout parent to persist across tab switches.
 *
 * Fields:
 * - Title (required)
 * - Description
 * - Event Type (appointment, meeting, task, reminder, time_block)
 * - Category (external, internal, personal)
 * - Date & Time
 * - All Day toggle
 * - Location Type & Address/URL
 * - Customer (for external events)
 * - Invoice (optional link)
 * - Status
 */
const EventDetailsTab = ({
  event,
  onSave,
  onClose,
  onUnsavedChanges,
  onEventTypeChange,
  // Optional: parent-owned status write (keeps calendar list in sync).
  // Falls back to eventService.changeStatus when omitted (e.g. shift editor).
  onStatusChange,
  // Lifted state from parent (persists across tab switches)
  formData,
  setFormData,
  selectedCustomerId,
  setSelectedCustomerId,
  invitees,
  setInvitees,
  // Shift mode - for Company > Scheduling > Shifts tab
  shiftMode = false
}) => {
  const isNewEvent = !event?.id;

  // Get current user's employee info for RSVP
  const { employee: currentEmployee } = useCompany();
  const { hasPermission } = usePermissions();
  const canViewAllScheduling = hasPermission(PERMISSIONS.scheduling.view);

  // Local UI state (doesn't need to persist across tab switches)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // RSVP state for current user
  const [isRespondingRSVP, setIsRespondingRSVP] = useState(false);
  const [currentUserResponse, setCurrentUserResponse] = useState(event?.currentUserResponseStatus || null);

  // Task completion toggle state
  const [isTogglingTaskStatus, setIsTogglingTaskStatus] = useState(false);

  // Invitees UI state (for meetings) - employees list and UI controls
  const [employees, setEmployees] = useState([]);
  const [selectedInviteeId, setSelectedInviteeId] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [isAddingInvitee, setIsAddingInvitee] = useState(false);
  const [isRemovingInvitee, setIsRemovingInvitee] = useState(null); // Track which invitee is being removed

  // Customer UI state (for appointments/tasks/reminders) - customers list
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Allowed breaks state (for shifts)
  // null = use company/role defaults, [] = no breaks, array = custom break rules
  const [allowedBreaks, setAllowedBreaks] = useState(null);
  const [useDefaultBreaks, setUseDefaultBreaks] = useState(true);
  const [companyBreakDefaults, setCompanyBreakDefaults] = useState([]);
  const [loadingBreakDefaults, setLoadingBreakDefaults] = useState(false);

  // Work location state (for shifts - Phase 9)
  const [companyLocations, setCompanyLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(event?.locationId || '');
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Note: formData initialization is now handled by parent EventSlideout
  // This component receives formData and setFormData as props

  // Track unsaved changes
  useEffect(() => {
    if (onUnsavedChanges) {
      const hasChanges = isNewEvent
        ? formData.title.trim() !== '' || invitees.length > 0
        : JSON.stringify(formData) !== JSON.stringify(getInitialFormData());
      onUnsavedChanges(hasChanges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isNewEvent, onUnsavedChanges, invitees.length]);

  // Fetch employees for invitees picker (meetings) or assignee picker (shifts)
  // Note: invitees state is lifted to parent, we only fetch the employee list here
  useEffect(() => {
    const fetchEmployees = async () => {
      // Load employees for meetings OR shifts
      if (formData.eventType !== 'meeting' && formData.eventType !== 'shift') return;

      try {
        setLoadingEmployees(true);
        const response = await employeeService.getDirectory({ status: 'active' });
        if (response.success) {
          setEmployees(response.employees || []);
        }

        // For existing events, fetch current invitees/assignees only if not already loaded
        // Parent initializes invitees to [], so we only fetch if empty and event exists
        if (event?.id && invitees.length === 0) {
          const assigneesResponse = await eventService.getAssignees(event.id);
          if (assigneesResponse.success) {
            if (formData.eventType === 'meeting') {
              // Filter to only those with requiresResponse (invitees for meetings)
              const meetingInvitees = (assigneesResponse.assignees || [])
                .filter(a => a.requiresResponse);
              setInvitees(meetingInvitees);
            } else if (formData.eventType === 'shift') {
              // For shifts, get all assignees
              setInvitees(assigneesResponse.assignees || []);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching employees:', err);
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.eventType, event?.id]);

  // Fetch customers for appointments/tasks/reminders picker
  // Note: selectedCustomerId state is lifted to parent, we only fetch the customers list here
  useEffect(() => {
    const fetchCustomers = async () => {
      const needsCustomer = ['appointment', 'task', 'reminder'].includes(formData.eventType);
      if (!needsCustomer) return;

      try {
        setLoadingCustomers(true);
        const customersList = await customersService.getCustomers();
        setCustomers(customersList || []);
        // Note: selectedCustomerId is initialized by parent from event.customerId
        // No need to set it here - parent handles initial state
      } catch (err) {
        console.error('Error fetching customers:', err);
      } finally {
        setLoadingCustomers(false);
      }
    };

    fetchCustomers();
  }, [formData.eventType]);

  // Fetch company locations for shifts (Phase 9 - Shift Location Assignment)
  useEffect(() => {
    const fetchLocations = async () => {
      if (!shiftMode) return;

      try {
        setLoadingLocations(true);
        const response = await eventService.getLocations();
        setCompanyLocations(response.locations || []);
        // If editing existing shift, set the selected location
        if (event?.locationId) {
          setSelectedLocationId(event.locationId);
        }
      } catch (err) {
        console.error('Error fetching company locations:', err);
        setCompanyLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftMode, event?.id]);

  // Fetch break defaults for shifts (using Global Settings infrastructure)
  useEffect(() => {
    const fetchBreakDefaults = async () => {
      if (!shiftMode) return;

      try {
        setLoadingBreakDefaults(true);
        // Use Global Settings service to get break allowances
        const result = await settingsService.getSetting('time_clock.break_allowances');
        setCompanyBreakDefaults(result?.value || []);

        // If editing existing shift, check for per-shift overrides
        if (event?.allowedBreaks !== undefined && event?.allowedBreaks !== null) {
          setAllowedBreaks(event.allowedBreaks);
          setUseDefaultBreaks(false);
        } else {
          setAllowedBreaks(null);
          setUseDefaultBreaks(true);
        }
      } catch (err) {
        console.error('Error fetching break defaults:', err);
        setCompanyBreakDefaults([]);
      } finally {
        setLoadingBreakDefaults(false);
      }
    };

    fetchBreakDefaults();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftMode, event?.id]);

  // Get category from event type
  const getCategoryFromEventType = (eventType) => {
    switch (eventType) {
      case 'appointment':
        return 'external';
      case 'meeting':
      case 'task':
        return 'internal';
      case 'reminder':
      case 'time_block':
        return 'personal';
      case 'shift':
        return 'schedule';
      default:
        return 'external';
    }
  };

  // Get initial form data for comparison
  const getInitialFormData = () => {
    if (!event) return formData;
    const startDate = event.startTime ? parseISO(event.startTime) : new Date();
    const endDate = event.endTime ? parseISO(event.endTime) : new Date();
    return {
      title: event.title || '',
      description: event.description || '',
      eventType: event.eventType || 'appointment',
      category: event.category || 'external',
      startDate: format(startDate, 'yyyy-MM-dd'),
      startTime: event.allDay ? '00:00' : format(startDate, 'HH:mm'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      endTime: event.allDay ? '23:59' : format(endDate, 'HH:mm'),
      allDay: event.allDay || false,
      locationType: event.locationType || '',
      locationAddress: event.locationAddress || '',
      locationUrl: event.locationUrl || '',
      status: event.status || 'scheduled',
      color: event.color || '',
      visibility: event.visibility || 'company'
    };
  };

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => {
      const updates = { [field]: value };

      // Auto-sync end date when start date moves forward
      // This prevents users from forgetting to update end date
      if (field === 'startDate' && value > prev.endDate) {
        updates.endDate = value;
      }

      return { ...prev, ...updates };
    });

    // Auto-set category when event type changes
    if (field === 'eventType') {
      setFormData(prev => ({
        ...prev,
        category: getCategoryFromEventType(value)
      }));
      // Notify parent of event type change for dynamic tabs
      if (onEventTypeChange) {
        onEventTypeChange(value);
      }
      // Clear invitees/customer when switching event types
      setInvitees([]);
      setSelectedCustomerId('');
    }
  };

  // Handle add invitee (for meetings)
  const handleAddInvitee = async () => {
    if (!selectedInviteeId || isAddingInvitee) return;

    const employee = employees.find(e => e.id === selectedInviteeId);
    if (!employee) return;

    // Prevent race condition - check if already added
    if (invitees.some(i => i.employeeId === selectedInviteeId || i.employeeId === employee.id)) {
      setSelectedInviteeId('');
      return;
    }

    setIsAddingInvitee(true);

    // For existing events, call API to add
    if (event?.id) {
      try {
        const response = await eventService.addAssignee(event.id, selectedInviteeId, {
          isOrganizer: false, // Creator is already the organizer (set by backend)
          requiresResponse: true // Always true for meeting invitees
        });

        if (response.success) {
          setInvitees(prev => [...prev, {
            ...response.assignee,
            employee: employee
          }]);
        }
      } catch (err) {
        console.error('Error adding invitee:', err);
        setError(getApiErrorMessage(err, 'Failed to add invitee'));
        setIsAddingInvitee(false);
        return;
      }
    } else {
      // For new events, just add to local state
      // Creator will be set as organizer by backend on save
      setInvitees(prev => [...prev, {
        employeeId: selectedInviteeId,
        employee: employee,
        requiresResponse: true,
        isOrganizer: false // Creator is the organizer, not invitees
      }]);
    }

    setSelectedInviteeId('');
    setIsAddingInvitee(false);
    if (onUnsavedChanges) onUnsavedChanges(true);
  };

  // Handle remove invitee
  const handleRemoveInvitee = async (employeeId) => {
    // Prevent double-click race condition
    if (isRemovingInvitee === employeeId) return;

    setIsRemovingInvitee(employeeId);

    // For existing events, call API first
    if (event?.id) {
      try {
        await eventService.removeAssignee(event.id, employeeId);
      } catch (err) {
        console.error('Error removing invitee:', err);
        setError('Failed to remove invitee');
        setIsRemovingInvitee(null);
        return; // Don't update local state if API call fails
      }
    }

    // Update local state (for new events this is the only action,
    // for existing events this runs after successful API call)
    setInvitees(prev => prev.filter(i => String(i.employeeId) !== String(employeeId)));
    setIsRemovingInvitee(null);
    if (onUnsavedChanges) onUnsavedChanges(true);
  };

  // Get available employees (not already invited)
  // Use String comparison to handle integer/string ID mismatches
  const availableEmployees = employees.filter(
    emp => !invitees.some(i => String(i.employeeId) === String(emp.id))
  );

  // Handle RSVP response (Accept/Tentative/Decline)
  const handleRSVPResponse = async (responseStatus) => {
    if (!event?.id || !currentEmployee?.id || isRespondingRSVP) return;

    setIsRespondingRSVP(true);
    setError(null);

    try {
      const response = await eventService.updateAssigneeResponse(
        event.id,
        currentEmployee.id,
        responseStatus
      );

      if (response.success) {
        setCurrentUserResponse(responseStatus);
        // If declined, close the slideout as the event will be hidden
        if (responseStatus === 'declined') {
          onClose();
        }
      }
    } catch (err) {
      console.error('Error updating RSVP response:', err);
      setError('Failed to update your response. Please try again.');
    } finally {
      setIsRespondingRSVP(false);
    }
  };

  // Check if current user should see RSVP options
  // Show if: not organizer AND requires response AND has an existing event
  const showRSVPSection = !isNewEvent &&
    event?.currentUserRequiresResponse &&
    !event?.currentUserIsOrganizer &&
    currentEmployee?.id;

  // Handle task completion toggle (scheduled ↔ completed).
  // Prefer parent onStatusChange so CalendarView's events list stays in sync;
  // otherwise call the status API directly (shift editor / standalone hosts).
  const handleToggleTaskStatus = async () => {
    if (!event?.id || isTogglingTaskStatus) return;

    const newStatus = formData.status === 'completed' ? 'scheduled' : 'completed';
    setIsTogglingTaskStatus(true);
    setError(null);

    try {
      if (onStatusChange) {
        await onStatusChange(event.id, newStatus);
      } else {
        const response = await eventService.changeStatus(event.id, newStatus);
        if (!response.success) {
          throw new Error(response.error || 'Failed to change event status');
        }
      }
      // Keep form + panel on the same event; only the status flips.
      setFormData(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Error toggling task status:', err);
      setError('Failed to update task status. Please try again.');
    } finally {
      setIsTogglingTaskStatus(false);
    }
  };

  // Check if task completion toggle should be shown
  // Show for existing tasks only
  const showTaskCompletionToggle = !isNewEvent && formData.eventType === 'task';
  const isTaskCompleted = formData.status === 'completed';

  // Get response status badge for invitees
  const getResponseBadge = (status) => {
    const badges = {
      accepted: { bg: 'bg-green-100', text: 'text-green-800', label: 'Accepted' },
      declined: { bg: 'bg-red-100', text: 'text-red-800', label: 'Declined' },
      tentative: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Tentative' }
    };
    return badges[status] || { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Pending' };
  };

  // Build event data for submission
  const buildEventData = () => {
    const startDateTime = new Date(`${formData.startDate}T${formData.allDay ? '00:00' : formData.startTime}`);
    const endDateTime = new Date(`${formData.endDate}T${formData.allDay ? '23:59' : formData.endTime}`);

    // For shifts, auto-generate title from employee name.
    // Invitees may nest `.employee` (picker / API) or carry flat name fields (legacy drafts).
    let title = formData.title.trim();
    if (shiftMode && invitees.length > 0) {
      const inv = invitees[0];
      const emp = inv.employee || inv;
      const first = emp?.firstName || '';
      const last = emp?.lastName || '';
      const fromInvitee = `${first} ${last}`.trim();
      title = fromInvitee || title || 'Shift';
    }

    const eventData = {
      id: event?.id || null,
      title,
      description: formData.description.trim() || null,
      eventType: formData.eventType,
      category: getCategoryFromEventType(formData.eventType),
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      allDay: formData.allDay,
      locationType: formData.locationType || null,
      locationAddress: formData.locationAddress.trim() || null,
      locationUrl: formData.locationUrl.trim() || null,
      status: formData.status,
      color: formData.color || null,
      visibility: formData.visibility
    };

    // Include customer for appointments/tasks/reminders
    if (['appointment', 'task', 'reminder'].includes(formData.eventType) && selectedCustomerId) {
      eventData.customerId = selectedCustomerId;
    }

    // Include invitees for new meetings (existing events handle invitees via API)
    if (formData.eventType === 'meeting' && !event?.id && invitees.length > 0) {
      eventData.invitees = invitees.map(i => ({
        employeeId: i.employeeId,
        isOrganizer: i.isOrganizer,
        requiresResponse: true
      }));
    }

    // Include assignees for new shifts (existing events handle assignees via API)
    if (formData.eventType === 'shift' && !event?.id && invitees.length > 0) {
      eventData.assignees = invitees.map(i => ({
        employeeId: i.employeeId,
        isOrganizer: false,
        requiresResponse: false
      }));
    }

    // Include allowed breaks for shifts (null = use defaults)
    if (formData.eventType === 'shift') {
      eventData.allowedBreaks = useDefaultBreaks ? null : allowedBreaks;
      // Include work location for labor law tracking (Phase 9)
      eventData.locationId = selectedLocationId || null;
    }

    return eventData;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Title required except for shifts (shifts auto-generate title from employee name)
    if (!shiftMode && !formData.title.trim()) {
      setError('Please enter a title');
      return;
    }

    // Shifts require an employee assigned
    if (formData.eventType === 'shift' && invitees.length === 0) {
      setError('Please select an employee for this shift');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const eventData = buildEventData();
      await onSave(eventData);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save event'));
      setIsSubmitting(false);
    }
  };

  const eventTypes = [
    { value: 'appointment', label: 'Appointment', description: 'Service visit or on-site job' },
    { value: 'meeting', label: 'Meeting', description: 'Call or virtual session' },
    { value: 'task', label: 'Task', description: 'To-do item' },
    { value: 'reminder', label: 'Reminder', description: 'Personal reminder' },
    { value: 'time_block', label: 'Time Block', description: 'Focus time' },
    { value: 'shift', label: 'Shift', description: 'Employee work schedule' }
  ];

  const locationTypes = [
    { value: '', label: 'None' },
    { value: 'in_person', label: 'In Person' },
    { value: 'virtual', label: 'Virtual' },
    { value: 'phone', label: 'Phone' }
  ];

  const statuses = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  return (
    <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6 overflow-x-hidden">
      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* RSVP Section - For invitees to respond to event invitations */}
      {showRSVPSection && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Your Response</h4>
              <p className="text-xs text-gray-500 mt-0.5">
                {currentUserResponse
                  ? `Current: ${currentUserResponse.charAt(0).toUpperCase() + currentUserResponse.slice(1)}`
                  : 'You have been invited to this event'}
              </p>
            </div>
            {currentUserResponse && (
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                currentUserResponse === 'accepted' ? 'bg-green-100 text-green-700' :
                currentUserResponse === 'tentative' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {currentUserResponse === 'accepted' ? 'Accepted' :
                 currentUserResponse === 'tentative' ? 'Tentative' : 'Pending'}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleRSVPResponse('accepted')}
              disabled={isRespondingRSVP || currentUserResponse === 'accepted'}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors
                ${currentUserResponse === 'accepted'
                  ? 'bg-green-500 text-white cursor-default'
                  : 'bg-white border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50'
                }`}
            >
              <CheckIcon className="h-4 w-4" />
              Accept
            </button>
            <button
              type="button"
              onClick={() => handleRSVPResponse('tentative')}
              disabled={isRespondingRSVP || currentUserResponse === 'tentative'}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors
                ${currentUserResponse === 'tentative'
                  ? 'bg-brand text-white cursor-default'
                  : 'bg-white border border-brand text-brand hover:bg-brand-light disabled:opacity-50'
                }`}
            >
              <QuestionMarkCircleIcon className="h-4 w-4" />
              Tentative
            </button>
            <button
              type="button"
              onClick={() => handleRSVPResponse('declined')}
              disabled={isRespondingRSVP}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors
                bg-white border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <XCircleIcon className="h-4 w-4" />
              Decline
            </button>
          </div>
          {isRespondingRSVP && (
            <p className="text-xs text-gray-500 mt-2 text-center">Updating your response...</p>
          )}
        </div>
      )}

      {/* Task Completion Toggle - For existing tasks */}
      {showTaskCompletionToggle && (
        <button
          type="button"
          onClick={handleToggleTaskStatus}
          disabled={isTogglingTaskStatus}
          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
            isTaskCompleted
              ? 'bg-green-50 border-green-200 hover:bg-green-100'
              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
          } disabled:opacity-50`}
        >
          {isTaskCompleted ? (
            <CheckCircleSolidIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
          ) : (
            <div className="h-6 w-6 rounded-full border-2 border-gray-300 flex-shrink-0" />
          )}
          <span className={`text-sm font-medium ${isTaskCompleted ? 'text-green-700' : 'text-gray-700'}`}>
            {isTogglingTaskStatus
              ? 'Updating...'
              : isTaskCompleted
                ? 'Reopen'
                : 'Mark Complete'}
          </span>
        </button>
      )}

      {/* Employee Assignment (Shifts) - Single employee per shift - SHOWN FIRST in shift mode */}
      {shiftMode && (
        <div>
          <label htmlFor="shiftEmployeeFirst" className="block text-sm font-medium text-gray-700 mb-1">
            <UserIcon className="inline-block w-4 h-4 mr-1 -mt-0.5" />
            Employee <span className="text-red-500">*</span>
          </label>
          <select
            id="shiftEmployeeFirst"
            value={invitees.length > 0 ? invitees[0].employeeId : ''}
            onChange={(e) => {
              const employeeId = e.target.value;
              if (!employeeId) {
                setInvitees([]);
                return;
              }
              const employee = employees.find(emp => String(emp.id) === String(employeeId));
              if (employee) {
                setInvitees([{
                  employeeId: employee.id,
                  employee: employee,
                  isOrganizer: false,
                  requiresResponse: false
                }]);
              }
            }}
            disabled={loadingEmployees}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent disabled:bg-gray-100"
          >
            <option value="">
              {loadingEmployees ? 'Loading...' : 'Select employee...'}
            </option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName}
                {emp.jobTitle ? ` - ${emp.jobTitle}` : ''}
              </option>
            ))}
          </select>
          {invitees.length === 0 && !loadingEmployees && (
            <p className="mt-1 text-sm text-red-500">Please select an employee for this shift</p>
          )}
        </div>
      )}

      {/* Work Location (Shifts - Phase 9) */}
      {shiftMode && companyLocations.length > 0 && (
        <div>
          <label htmlFor="shiftLocation" className="block text-sm font-medium text-gray-700 mb-1">
            Work Location
            <span className="text-xs text-gray-400 ml-1">(for labor law compliance)</span>
          </label>
          <select
            id="shiftLocation"
            value={selectedLocationId}
            onChange={(e) => setSelectedLocationId(e.target.value)}
            disabled={loadingLocations}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent disabled:bg-gray-100"
          >
            <option value="">
              {loadingLocations ? 'Loading...' : 'Use employee default location'}
            </option>
            {companyLocations.map(loc => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
                {loc.city && loc.state ? ` (${loc.city}, ${loc.state})` : loc.state ? ` (${loc.state})` : ''}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Determines overtime and break rules for this shift
          </p>
        </div>
      )}

      {/* Title - hidden in shift mode */}
      {!shiftMode && (
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            placeholder="Event title"
          />
        </div>
      )}

      {/* Description - hidden in shift mode */}
      {!shiftMode && (
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
            placeholder="Add details..."
          />
        </div>
      )}

      {/* Event Type - hidden in shift mode */}
      {!shiftMode && (
        <div>
          <label htmlFor="eventType" className="block text-sm font-medium text-gray-700 mb-1">
            Event Type
          </label>
          <select
            id="eventType"
            value={formData.eventType}
            onChange={(e) => handleChange('eventType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
          >
            {eventTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label} — {type.description}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Visibility - Company Wide checkbox for managers/admins only */}
      {!shiftMode && canViewAllScheduling && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="companyWide"
            checked={formData.visibility === 'company'}
            onChange={(e) => handleChange('visibility', e.target.checked ? 'company' : 'private')}
            className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
          />
          <label htmlFor="companyWide" className="text-sm text-gray-700">
            Show on all employee calendars
          </label>
        </div>
      )}

      {/* All Day Toggle - hidden in shift mode */}
      {!shiftMode && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="allDay"
            checked={formData.allDay}
            onChange={(e) => handleChange('allDay', e.target.checked)}
            className="rounded border-gray-300 text-brand focus:ring-brand"
          />
          <label htmlFor="allDay" className="text-sm text-gray-700">
            All day event
          </label>
        </div>
      )}

      {/* Date & Time */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
            Start
          </label>
          <div className="flex flex-col gap-2">
            <input
              type="date"
              id="startDate"
              value={formData.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            />
            {!formData.allDay && (
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => handleChange('startTime', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
              />
            )}
          </div>
        </div>
        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
            End
          </label>
          <div className="flex flex-col gap-2">
            <input
              type="date"
              id="endDate"
              value={formData.endDate}
              onChange={(e) => handleChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            />
            {!formData.allDay && (
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => handleChange('endTime', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
              />
            )}
          </div>
        </div>
      </div>

      {/* Location - hidden in shift mode */}
      {!shiftMode && (
        <div>
          <label htmlFor="locationType" className="block text-sm font-medium text-gray-700 mb-1">
            Location
          </label>
          <select
            id="locationType"
            value={formData.locationType}
            onChange={(e) => handleChange('locationType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent mb-2"
          >
            {locationTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {formData.locationType === 'in_person' && (
            <input
              type="text"
              value={formData.locationAddress}
              onChange={(e) => handleChange('locationAddress', e.target.value)}
              placeholder="Enter address"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            />
          )}
          {formData.locationType === 'virtual' && (
            <input
              type="url"
              value={formData.locationUrl}
              onChange={(e) => handleChange('locationUrl', e.target.value)}
              placeholder="Enter meeting URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            />
          )}
          {formData.locationType === 'phone' && (
            <input
              type="tel"
              value={formData.locationAddress}
              onChange={(e) => handleChange('locationAddress', e.target.value)}
              placeholder="Enter phone number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            />
          )}
        </div>
      )}

      {/* Add Invitees Section (Meetings Only) */}
      {formData.eventType === 'meeting' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <UsersIcon className="inline-block w-4 h-4 mr-1 -mt-0.5" />
            Add Invitees
          </label>

          {/* Add Invitee Picker */}
          <div className="flex gap-2 mb-3">
            <select
              value={selectedInviteeId}
              onChange={(e) => setSelectedInviteeId(e.target.value)}
              disabled={loadingEmployees || availableEmployees.length === 0}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent disabled:bg-gray-100"
            >
              <option value="">
                {loadingEmployees
                  ? 'Loading...'
                  : availableEmployees.length === 0
                    ? 'All team members invited'
                    : 'Select team member...'}
              </option>
              {availableEmployees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                  {emp.jobTitle ? ` - ${emp.jobTitle}` : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAddInvitee}
              disabled={!selectedInviteeId || loadingEmployees || isAddingInvitee}
              className="px-3 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isAddingInvitee ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-800"></div>
              ) : (
                <PlusIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Current Invitees List */}
          {invitees.length > 0 && (
            <div className="space-y-2">
              {invitees.map((invitee) => {
                // Use String comparison to handle integer/string ID mismatches
                const employee = invitee.employee || employees.find(e => String(e.id) === String(invitee.employeeId));
                const responseBadge = getResponseBadge(invitee.responseStatus);

                return (
                  <div
                    key={invitee.employeeId}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-500 text-white text-xs font-medium">
                        {employee?.firstName?.[0]}{employee?.lastName?.[0]}
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          {employee?.firstName} {employee?.lastName}
                        </span>
                        {invitee.isOrganizer && (
                          <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            Organizer
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {invitee.responseStatus && (
                        <span className={`text-xs px-2 py-0.5 rounded ${responseBadge.bg} ${responseBadge.text}`}>
                          {responseBadge.label}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveInvitee(invitee.employeeId)}
                        disabled={isRemovingInvitee === invitee.employeeId}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                        title="Remove invitee"
                      >
                        {isRemovingInvitee === invitee.employeeId ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                        ) : (
                          <XMarkIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {invitees.length === 0 && !loadingEmployees && (
            <p className="text-sm text-gray-500 italic">No invitees added yet</p>
          )}
        </div>
      )}

      {/* Allowed Breaks Section (Shifts only) */}
      {shiftMode && (
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              <ClockIcon className="inline-block w-4 h-4 mr-1 -mt-0.5" />
              Allowed Breaks
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={useDefaultBreaks}
                onChange={(e) => {
                  setUseDefaultBreaks(e.target.checked);
                  if (e.target.checked) {
                    setAllowedBreaks(null); // Use defaults
                  } else {
                    // Start with company defaults as template
                    setAllowedBreaks([...companyBreakDefaults]);
                  }
                }}
                className="rounded border-gray-300 text-brand focus:ring-brand"
              />
              <span className="text-gray-600">Use company defaults</span>
            </label>
          </div>

          {loadingBreakDefaults ? (
            <div className="text-sm text-gray-500 py-2">Loading break settings...</div>
          ) : useDefaultBreaks ? (
            <div className="bg-gray-50 rounded-lg p-3">
              {companyBreakDefaults.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No company break defaults configured. Employees can log breaks freely.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 mb-2">Company defaults:</p>
                  {companyBreakDefaults.map((brk, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{brk.name}</span>
                      <span className="text-gray-500">
                        {brk.durationMinutes} min × {brk.countAllowed}
                        {brk.isPaid && <span className="ml-1 text-green-600">(paid)</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Custom break rules */}
              {(allowedBreaks || []).map((brk, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2">
                  {/* Row 1: Name and delete button */}
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={brk.name}
                      onChange={(e) => {
                        const updated = [...allowedBreaks];
                        updated[index] = { ...updated[index], name: e.target.value };
                        setAllowedBreaks(updated);
                      }}
                      placeholder="Break name"
                      className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-brand focus:border-brand"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setAllowedBreaks(allowedBreaks.filter((_, i) => i !== index));
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Row 2: Duration, count, and paid */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={brk.durationMinutes}
                        onChange={(e) => {
                          const updated = [...allowedBreaks];
                          updated[index] = { ...updated[index], durationMinutes: parseInt(e.target.value) || 0 };
                          setAllowedBreaks(updated);
                        }}
                        placeholder="15"
                        min="1"
                        className="w-14 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-brand focus:border-brand"
                      />
                      <span className="text-xs text-gray-500">min</span>
                    </div>
                    <span className="text-gray-400">×</span>
                    <input
                      type="number"
                      value={brk.countAllowed}
                      onChange={(e) => {
                        const updated = [...allowedBreaks];
                        updated[index] = { ...updated[index], countAllowed: parseInt(e.target.value) || 1 };
                        setAllowedBreaks(updated);
                      }}
                      placeholder="1"
                      min="1"
                      className="w-12 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-brand focus:border-brand"
                    />
                    <label className="flex items-center gap-1.5 text-xs ml-auto">
                      <input
                        type="checkbox"
                        checked={brk.isPaid}
                        onChange={(e) => {
                          const updated = [...allowedBreaks];
                          updated[index] = { ...updated[index], isPaid: e.target.checked };
                          setAllowedBreaks(updated);
                        }}
                        className="rounded border-gray-300 text-green-500 focus:ring-green-400"
                      />
                      <span className="text-gray-600">Paid</span>
                    </label>
                  </div>
                </div>
              ))}

              {/* Add break button */}
              <button
                type="button"
                onClick={() => {
                  setAllowedBreaks([
                    ...(allowedBreaks || []),
                    { name: '', durationMinutes: 15, countAllowed: 1, isPaid: false }
                  ]);
                }}
                className="flex items-center gap-1 text-sm text-brand hover:text-brand-hover"
              >
                <PlusIcon className="w-4 h-4" />
                Add break type
              </button>

              {(allowedBreaks || []).length === 0 && (
                <p className="text-sm text-gray-500 italic">
                  No breaks configured. Employee can log breaks freely for this shift.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Customer Selection (Appointments/Tasks/Reminders) */}
      {['appointment', 'task', 'reminder'].includes(formData.eventType) && (
        <div>
          <label htmlFor="customer" className="block text-sm font-medium text-gray-700 mb-1">
            <UserIcon className="inline-block w-4 h-4 mr-1 -mt-0.5" />
            Customer {formData.eventType === 'appointment' && <span className="text-gray-400">(recommended)</span>}
          </label>
          <select
            id="customer"
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            disabled={loadingCustomers}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent disabled:bg-gray-100"
          >
            <option value="">
              {loadingCustomers ? 'Loading...' : 'Select customer (optional)'}
            </option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim()}
                {customer.email ? ` - ${customer.email}` : ''}
              </option>
            ))}
          </select>
          {formData.eventType === 'appointment' && !selectedCustomerId && (
            <p className="mt-1 text-xs text-gray-500">
              Linking a customer allows you to notify them about this appointment
            </p>
          )}
        </div>
      )}

      {/* Status (only for existing events, not for shifts) */}
      {!isNewEvent && !shiftMode && (
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="status"
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
          >
            {statuses.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || (!shiftMode && !formData.title.trim()) || (shiftMode && invitees.length === 0)}
          className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? 'Saving...' : shiftMode ? (isNewEvent ? 'Create Shift' : 'Save Shift') : (isNewEvent ? 'Create Event' : 'Save Changes')}
        </button>
      </div>
    </form>
  );
};

export default EventDetailsTab;
